import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as stylex from '@stylexjs/stylex';
import { clsx } from 'clsx';
import { getQTipData } from '../Tooltip/qTip.utilities';
import { menuStyles, overlayStyles } from '../styles/overlays';
import { colors } from '../theme/colors.stylex';
import { InsertSeeqContent, SeeqActionDropdownItem, ViewWorkbench } from './variants';
import type { QTipDataAttributes } from '../Tooltip/QTip.types';
import type { SeeqActionDropdownItems, SeeqActionDropdownProps } from './SeeqActionDropdown.types';

const styles = stylex.create({
  trigger: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderStyle: 'none',
    outlineStyle: { 'default': null, ':focus-within': 'none', ':focus': 'none', ':focus-visible': 'none' },
  },
  disabled: { cursor: 'not-allowed', opacity: 0.5 },
  content: { borderRadius: '0.375rem', boxShadow: '0 4px 8px rgba(0,0,0,0.2)' },
  item: {
    position: 'relative',
    display: 'flex',
    cursor: 'pointer',
    outlineStyle: 'none',
    userSelect: 'none',
    backgroundColor: { 'default': 'transparent', ':hover': colors.menuHover },
    color: colors.menuText,
  },
  dim: { opacity: 0.5 },
  disabledItem: { pointerEvents: 'none', color: colors.menuTextDisabled },
});

const renderItem = (variant: SeeqActionDropdownProps['variant'], item: SeeqActionDropdownItems) => {
  switch (variant) {
    case 'create-workbench':
      return <SeeqActionDropdownItem {...item} />;
    case 'view-workbench':
      return <ViewWorkbench {...item} />;
    case 'insert-seeq-content':
      return <InsertSeeqContent {...item} />;
    default:
      return <SeeqActionDropdownItem {...item} />;
  }
};

const SeeqActionDropdown = ({
  seeqActionDropdownItems,
  trigger,
  id,
  extraClassNames,
  xstyle,
  contentXstyle,
  containerTestId,
  disabled = false,
  align = 'end',
  placement = 'bottom',
  placementOffset = 5,
  alignOffset = -35,
  hasArrow = false,
  onOpenChange,
  isOpen,
  setFocusOnTriggerOnClose = true,
  keepFocusInsideDropdown = true,
  variant,
  ...tooltipProps
}: SeeqActionDropdownProps) => {
  const tooltipData: QTipDataAttributes | undefined = getQTipData(tooltipProps);

  const triggerProps = stylex.props(styles.trigger, disabled && styles.disabled, xstyle);
  return (
    <DropdownMenu.Root defaultOpen={false} open={isOpen} onOpenChange={onOpenChange} modal={keepFocusInsideDropdown}>
      <DropdownMenu.Trigger id={id} data-testid={id} asChild disabled={disabled}>
        <div {...tooltipData} {...triggerProps} className={clsx(triggerProps.className, extraClassNames)}>
          {trigger}
        </div>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        sideOffset={placementOffset}
        side={placement}
        align={align}
        alignOffset={alignOffset}
        asChild
        onCloseAutoFocus={(e) => !setFocusOnTriggerOnClose && e.preventDefault()}
        {...stylex.props(overlayStyles.outline)}>
        <div
          data-testid={containerTestId}
          {...stylex.props(
            overlayStyles.panel,
            overlayStyles.motion,
            menuStyles.content,
            styles.content,
            contentXstyle,
          )}>
          {hasArrow && (
            <DropdownMenu.Arrow asChild>
              <div {...stylex.props(overlayStyles.arrow)} />
            </DropdownMenu.Arrow>
          )}
          {seeqActionDropdownItems.map((item, index) => {
            return (
              <div key={item.id ?? index}>
                <DropdownMenu.Item
                  onSelect={(e) => {
                    item.action(e);
                  }}
                  {...stylex.props(styles.item, item.dim && styles.dim, !item.enabled && styles.disabledItem)}
                  data-testid={item.testId}
                  data-dim={item.dim ? '' : undefined}
                  disabled={!item.enabled}>
                  {renderItem(variant, item)}
                </DropdownMenu.Item>
                {item.divider && (
                  <DropdownMenu.Separator
                    data-testid={`dropdown-divider-${index}`}
                    {...stylex.props(menuStyles.separator)}
                  />
                )}
              </div>
            );
          })}
        </div>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

export default SeeqActionDropdown;
